import { useEffect, useRef, useState } from 'react';
import { rechercherPersonnes } from '../services/rechercheService';
import '../assets/styles/Mentions.css';

/**
 * Champ de saisie qui suggère des personnes quand on tape « @ ».
 *
 * « @mar » cherche, dans les formateurs, les participants, les clients et les
 * interlocuteurs des demandes, les personnes dont le nom, l'adresse, la
 * société ou toute autre information de la fiche contient « mar ». Choisir une
 * suggestion remplace « @mar » par une ligne exploitable —
 * « Marie DUPONT — marie@acme.fr — ACME » (le formateur : « … — Formateur ») —
 * que les lectures de texte de l'application savent relire (société, adresse,
 * qualité).
 *
 * Remplace un <textarea> ou un <input> sans changer la façon de l'utiliser :
 * `onChange` reçoit toujours un événement (celui du champ, ou un faux événement
 * `{ target: { value } }` quand c'est la suggestion qui modifie le texte).
 *
 * `as`        : 'textarea' (défaut) ou 'input'.
 * `suffixe`   : texte ajouté après la ligne insérée (« \n » pour une personne par ligne).
 * `onChoisir` : reçoit la personne choisie ; le « @… » est alors simplement retiré
 *               du texte, c'est l'appelant qui en fait ce qu'il veut (remplir une
 *               ligne de formulaire, par exemple).
 */

/** Un « @ » en début de mot, suivi de ce qu'on tape jusqu'au curseur (pas d'adresse : « a@b.fr »). */
const MOTIF = /(^|\s)@([^\n@]{0,40})$/;
const LONGUEUR_MIN = 2;
const DELAI_MS = 220;

const TYPES = {
  formateur: 'Formateur',
  participant: 'Participant',
  client: 'Client',
  demande: 'Demande',
};

/** « Marie DUPONT — marie@acme.fr — ACME » ; le formateur porte « Formateur » en dernier. */
export function lignePersonne(p) {
  const nom = [p.prenom, String(p.nom || '').toLocaleUpperCase('fr')].filter(Boolean).join(' ');
  const dernier = p.types?.includes('formateur') ? 'Formateur' : p.societe;
  return [nom, p.email, dernier].filter(Boolean).join(' — ');
}

function ChampMentions({
  as: Balise = 'textarea',
  value,
  onChange,
  onKeyDown,
  onBlur,
  onChoisir,
  suffixe = '',
  disabled,
  ...reste
}) {
  const champ = useRef(null);
  const [jeton, setJeton] = useState(null); // { debut, fin, requete } : le « @… » en cours
  const [suggestions, setSuggestions] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [actif, setActif] = useState(0);
  const numero = useRef(0);
  const minuteur = useRef(null);

  useEffect(() => () => clearTimeout(minuteur.current), []);

  function fermer() {
    clearTimeout(minuteur.current);
    numero.current += 1;
    setJeton(null);
    setSuggestions([]);
    setChargement(false);
  }

  function chercher(requete) {
    clearTimeout(minuteur.current);
    if (requete.trim().length < LONGUEUR_MIN) {
      numero.current += 1;
      setSuggestions([]);
      setChargement(false);
      return;
    }
    setChargement(true);
    minuteur.current = setTimeout(async () => {
      const mien = ++numero.current;
      try {
        const res = await rechercherPersonnes(requete.trim());
        // Une réponse arrivée après une frappe plus récente est périmée.
        if (mien !== numero.current) return;
        setSuggestions(res.data?.personnes || []);
        setActif(0);
      } catch {
        if (mien === numero.current) setSuggestions([]);
      } finally {
        if (mien === numero.current) setChargement(false);
      }
    }, DELAI_MS);
  }

  function surChangement(e) {
    onChange?.(e);
    const el = e.target;
    const caret = el.selectionStart ?? el.value.length;
    const trouve = MOTIF.exec(el.value.slice(0, caret));
    if (!trouve) {
      if (jeton) fermer();
      return;
    }
    const requete = trouve[2];
    setJeton({ debut: caret - requete.length - 1, fin: caret, requete });
    chercher(requete);
  }

  function choisir(personne) {
    if (!jeton) return;
    const texte = String(value ?? '');
    const insere = onChoisir ? '' : lignePersonne(personne) + suffixe;
    const suite = texte.slice(0, jeton.debut) + insere + texte.slice(jeton.fin);
    onChange?.({ target: { value: suite } });
    fermer();
    onChoisir?.(personne);
    // Le curseur se pose juste après ce qui a été inséré.
    const position = jeton.debut + insere.length;
    setTimeout(() => {
      const el = champ.current;
      if (el && el.setSelectionRange) {
        el.focus();
        el.setSelectionRange(position, position);
      }
    }, 0);
  }

  function surTouche(e) {
    if (jeton && suggestions.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActif((a) => (a + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActif((a) => (a - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        choisir(suggestions[actif]);
        return;
      }
    }
    if (jeton && e.key === 'Escape') {
      e.preventDefault();
      fermer();
      return;
    }
    onKeyDown?.(e);
  }

  const aide = jeton && jeton.requete.trim().length < LONGUEUR_MIN;
  const vide = jeton && !aide && !chargement && !suggestions.length;

  return (
    <div className="mentions">
      <Balise
        {...reste}
        ref={champ}
        value={value}
        disabled={disabled}
        onChange={surChangement}
        onKeyDown={surTouche}
        onBlur={(e) => {
          // Laisse le clic sur une suggestion se faire avant de fermer.
          setTimeout(fermer, 150);
          onBlur?.(e);
        }}
      />
      {jeton && (
        <div className="mentions-liste" role="listbox">
          {aide && (
            <div className="mentions-info">
              Tapez au moins {LONGUEUR_MIN} lettres : nom, e-mail, société…
            </div>
          )}
          {!aide && chargement && !suggestions.length && (
            <div className="mentions-info">Recherche…</div>
          )}
          {vide && <div className="mentions-info">Personne ne correspond à « {jeton.requete} ».</div>}
          {suggestions.map((p, i) => (
            <button
              type="button"
              role="option"
              aria-selected={i === actif}
              key={`${p.email || ''}|${p.prenom}|${p.nom}`}
              className={`mentions-item${i === actif ? ' mentions-item-actif' : ''}`}
              // mouseDown et non click : le champ ne doit pas perdre le focus avant le choix.
              onMouseDown={(e) => {
                e.preventDefault();
                choisir(p);
              }}
              onMouseEnter={() => setActif(i)}
            >
              <span className="mentions-nom">
                {[p.prenom, String(p.nom || '').toLocaleUpperCase('fr')].filter(Boolean).join(' ') ||
                  p.email}
              </span>
              <span className="mentions-detail">
                {[p.email, p.societe, p.detail].filter(Boolean).join(' · ')}
              </span>
              <span className="mentions-types">
                {(p.types || [p.type]).map((t) => (
                  <span key={t} className={`mentions-type mentions-type-${t}`}>
                    {TYPES[t] || t}
                  </span>
                ))}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ChampMentions;
